/** 10y−2y spread regime, momentum, and recession-signal copy (monthly bars). */

export const SIGNAL_DISCLAIMER =
  'Leading indicator only: US recessions since the late 1960s were often preceded by inversion, with variable 6–24 month lags and occasional false signals (e.g. 1966, 1998).';

export const EDUCATION_SECTIONS = [
  {
    title: 'Recession signal',
    body:
      'A recession signal is an indicator that has historically turned before economic contractions, so it warns ahead of time rather than confirming afterwards. The yield curve is the best-known one.',
  },
  {
    title: 'Why the 10y−2y slope works',
    body:
      'Normal (positive slope): investors demand more yield for locking money up longer — healthy, expansionary. Inverted (negative slope): the 2-year yield, which tracks expected Fed policy, rises above the 10-year. The market is saying policy is tight now and rates will have to fall later, which usually means growth will weaken. Banks borrow short and lend long; an inverted curve squeezes their margin, so credit tightens and activity slows.',
  },
  {
    title: 'Track record (US)',
    body:
      'Every recession since the late 1960s was preceded by an inversion. Lead time was long and variable, roughly 6 to 24 months. There were a few false or weak signals, such as 1966 and the brief 1998 dip.',
  },
  {
    title: 'Two refinements that matter',
    body:
      'The un-inversion is often the real warning: recessions have tended to start after the curve steepens back above zero. That steepening is usually driven by the 2-year falling fast as the Fed cuts — “bull steepening.”',
  },
];

const STEEP_THRESH_PP = 0.1;
const FLAT_THRESH_PP = 0.05;
const UNINVERSION_LOOKBACK_MONTHS = 18;

function deltaAt(bars, index, lag) {
  if (index < lag) return null;
  return bars[index].value - bars[index - lag].value;
}

/** Zero-cross events up to `endIndex` (inclusive). */
export function findZeroCrossingsUpTo(bars, endIndex = bars.length - 1) {
  const crossings = [];
  for (let i = 1; i <= endIndex; i += 1) {
    const prev = bars[i - 1].value;
    const curr = bars[i].value;
    if (prev >= 0 && curr < 0) {
      crossings.push({ time: bars[i].time, direction: 'down', index: i });
    } else if (prev < 0 && curr >= 0) {
      crossings.push({ time: bars[i].time, direction: 'up', index: i });
    }
  }
  return crossings;
}

function monthsAgoAtIndex(bars, fromIndex, toIndex) {
  return fromIndex - toIndex;
}

function wasInvertedWithin(bars, endIndex, months) {
  for (let i = endIndex; i >= 0 && endIndex - i <= months; i -= 1) {
    if (bars[i].value < 0) return true;
  }
  return false;
}

function lastCrossUpBefore(bars, endIndex) {
  const crossings = findZeroCrossingsUpTo(bars, endIndex).filter((c) => c.direction === 'up');
  return crossings.length ? crossings[crossings.length - 1] : null;
}

function momentumLabel(d3m) {
  if (d3m == null) return '—';
  if (d3m > STEEP_THRESH_PP) return 'steepening';
  if (d3m < -STEEP_THRESH_PP) return 'flattening';
  return 'flat';
}

function fmtPp(v) {
  if (v == null || Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)} pp`;
}

function formatMonth(iso) {
  const [y, m] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${y}`;
}

/**
 * Classify signal at a given bar index (use full length − 1 for “current”).
 * @param {Array<{ time: string, value: number }>} bars
 * @param {number} [index]
 */
export function classifyYieldCurveSignal(bars, index = bars.length - 1) {
  if (!bars?.length || index < 0 || index >= bars.length) {
    return {
      id: 'unknown',
      shortLabel: 'No data',
      summary: 'Load DGS10 and DGS2 to assess the curve.',
      cssClass: 'neutral',
      spread: null,
      inverted: false,
      momentum: { d1m: null, d3m: null, d12m: null, label: '—' },
      disclaimer: SIGNAL_DISCLAIMER,
    };
  }

  const spread = bars[index].value;
  const inverted = spread < 0;
  const d1m = deltaAt(bars, index, 1);
  const d3m = deltaAt(bars, index, 3);
  const d12m = deltaAt(bars, index, 12);
  const mom = momentumLabel(d3m);
  const crossUp = lastCrossUpBefore(bars, index);
  const recentlyUninverted =
    !inverted
    && wasInvertedWithin(bars, index, UNINVERSION_LOOKBACK_MONTHS)
    && d3m != null
    && d3m > FLAT_THRESH_PP;

  let id;
  let shortLabel;
  let summary;

  if (index < 3) {
    id = 'insufficient';
    shortLabel = 'Building history';
    summary = 'Need a few more monthly points to measure 3-month momentum.';
  } else if (recentlyUninverted) {
    id = 'post_uninversion_steepening';
    shortLabel = 'Post-inversion steepening';
    const since = crossUp ? formatMonth(crossUp.time) : 'recently';
    summary =
      `Spread back above zero (crossed up ${since}) and still steepening (${fmtPp(d3m)} over 3m). `
      + 'Historically, recessions have often followed this “un-inversion” phase — bull steepening as the 2-year falls on expected Fed cuts — not the first month of inversion alone.';
  } else if (inverted && d3m != null && d3m > FLAT_THRESH_PP) {
    id = 'inverted_flattening';
    shortLabel = 'Inverted · flattening toward zero';
    summary =
      `Still inverted (${spread.toFixed(2)} pp) but rising toward zero (${fmtPp(d3m)} over 3m). `
      + 'Late-cycle signal: policy tight, growth expected to weaken; watch for a move back above zero.';
  } else if (inverted && d3m != null && d3m < -FLAT_THRESH_PP) {
    id = 'inverted_deepening';
    shortLabel = 'Inverted · deepening';
    summary =
      `Curve more inverted (${fmtPp(d3m)} over 3m). `
      + 'Short rates above long rates — credit margins under pressure; historically associated with recession risk ahead (long, variable lag).';
  } else if (inverted) {
    id = 'inverted';
    shortLabel = 'Inverted';
    summary =
      '2y above 10y: tight policy priced near-term, weaker growth expected later. '
      + 'Classic recession-precursor signal, but timing is uncertain.';
  } else if (!inverted && d3m != null && d3m < -FLAT_THRESH_PP) {
    id = 'normal_flattening';
    shortLabel = 'Normal · flattening';
    summary =
      `Positive slope but compressing (${fmtPp(d3m)} over 3m). `
      + 'Late expansion watch — curve moving toward inversion if the trend continues.';
  } else if (!inverted && d3m != null && d3m > FLAT_THRESH_PP) {
    id = 'normal_steepening';
    shortLabel = 'Normal · steepening';
    summary =
      `Healthy positive slope widening (${fmtPp(d3m)} over 3m). `
      + 'Typical expansionary backdrop unless this follows a long inversion (see un-inversion case).';
  } else {
    id = 'normal_flat';
    shortLabel = inverted ? 'Inverted' : 'Normal · stable';
    summary = inverted
      ? 'Curve inverted with little 3-month change.'
      : 'Positive slope with stable 3-month momentum.';
  }

  const monthsSinceCrossUp =
    crossUp != null ? monthsAgoAtIndex(bars, index, crossUp.index) : null;

  return {
    id,
    shortLabel,
    summary,
    cssClass: cssForSignal(id),
    spread,
    inverted,
    time: bars[index].time,
    momentum: { d1m, d3m, d12m, label: mom },
    monthsSinceCrossUp,
    disclaimer: SIGNAL_DISCLAIMER,
  };
}

function cssForSignal(id) {
  switch (id) {
    case 'post_uninversion_steepening':
    case 'inverted_deepening':
      return 'warning';
    case 'inverted_flattening':
    case 'inverted':
    case 'normal_flattening':
      return 'caution';
    case 'normal_steepening':
    case 'normal_flat':
      return 'ok';
    default:
      return 'neutral';
  }
}

/** Hover tooltip: point-in-time read (no lookahead). */
export function classifyPointAtTime(bars, time) {
  const index = bars.findIndex((b) => b.time === time);
  if (index < 0) return null;
  return classifyYieldCurveSignal(bars, index);
}

export function formatSignalMomentumLine(signal) {
  if (!signal?.momentum) return '';
  const { d3m, label } = signal.momentum;
  if (d3m == null) return label;
  return `${label} · ${fmtPp(d3m)} (3m)`;
}

export function latestInversionSpell(bars) {
  if (!bars?.length) return null;
  let best = null;
  let start = null;
  let end = null;
  for (let i = 0; i < bars.length; i += 1) {
    if (bars[i].value < 0) {
      if (start == null) start = bars[i].time;
      end = bars[i].time;
    } else if (start != null) {
      best = { from: start, to: end };
      start = null;
      end = null;
    }
  }
  if (start != null) best = { from: start, to: end };
  return best;
}
